import { useLocale } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useSpravaSouhlasuSeznamContext } from '@gov-nx/module/page';
import {
	ContentLayout,
	ErrorStatusBanner,
	InfoBanner,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { RecallConsentPrompt } from '../RecallConsentPrompt';
import { ConsentManagementSkeleton } from './ConsentManagementSkeleton';
import { RecallButton } from './RecallButton';

interface ConsentManagementProps {
	code: PageCode;
}

export const ConsentManagementView = ({ code }: ConsentManagementProps) => {
	const {
		consentData,
		controls,
		formDefinition,
		isLowAuthorization,
		isAuthorizationEnforced,
		handleSubmit,
		setControls,
		onDialogClose,
	} = useSpravaSouhlasuSeznamContext();
	const { t } = useLocale(code);

	if (controls.initialLoading) {
		return <ConsentManagementSkeleton />;
	}

	if (controls.initialError) {
		return <ErrorStatusBanner code={code} />;
	}

	if (Array.isArray(consentData) && consentData.length === 0) {
		return (
			<ContentLayout>
				<InfoBanner
					headline={t('stav.chyba.zadny-zaznam')}
					text={t('stav.chyba.nic-jsme-nenasli')}
				/>
			</ContentLayout>
		);
	}

	return (
		<ContentLayout>
			<div>
				<Tiles>
					{consentData?.map((item) => (
						<Tile
							key={[item.id, item.ico].join(',')}
							name={item.nazevOsoby as string}
							headlineLink
							to={pageRouteResolver('sprava-souhlasu.detail', {
								consentId: item.id,
							})}>
							<p className="text-(--text-secondary)">
								{t('pole.udaje.id-souhlasu')}: <strong>{item.id}</strong>
							</p>
							<RecallButton
								onClick={() => {
									setControls({ consentToRecall: item });
								}}
								code={code}
							/>
						</Tile>
					))}
				</Tiles>
				<RecallConsentPrompt
					isOpen={!!controls.consentToRecall}
					isLowAuthorization={isLowAuthorization}
					isAuthorizationEnforced={isAuthorizationEnforced}
					formDefinition={formDefinition}
					isConsentRecalling={controls.processLoading}
					onSubmit={handleSubmit}
					onClose={onDialogClose}
				/>
			</div>
		</ContentLayout>
	);
};
